import { useEffect, useMemo } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ArrowLeft } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import { toISODate } from "@/lib/dates";
import { CATEGORY_STYLES, DEFAULT_CATEGORY_STYLE } from "@/lib/blogCategories";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import FinalCTA from "@/components/landing/FinalCTA";
import { BlogBlocks } from "@/components/landing/BlogBlocks";
import { BLOG_POSTS } from "@/data/blogPosts";

/** Pinta en naranja la sub-frase `highlight` dentro de `title` (si existe). */
function renderTitle(title, highlight) {
  if (!highlight) return title;
  const idx = title.indexOf(highlight);
  if (idx === -1) return title;
  return (
    <>
      {title.slice(0, idx)}
      <span className="text-[#F26522]">{highlight}</span>
      {title.slice(idx + highlight.length)}
    </>
  );
}

/** Convierte tramos **marcados** en negrita sutil y links [texto](url) en anchors externos. */
function renderBoldedText(text) {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-navy-800">
          {part.slice(2, -2)}
        </strong>
      );
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="blog-post-inline-link"
          className="text-[#F26522] font-semibold hover:underline"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return part;
  });
}

/**
 * Template de artículo — reutiliza el header, footer, medios de contacto y estética
 * del resto del sitio. El contenido viene de /app/frontend/src/data/blogPosts.js
 * Cada artículo vive en su propia URL raíz (ej: /responsabilidad-sin-microgestion),
 * definida explícitamente en App.js — por eso el slug se toma del pathname y no de un :param anidado.
 */
export default function BlogPost() {
  useReveal();
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    setPageSEO({
      title: post.seoTitle || `${post.title} | Rompiendo el Techo — Blog de Carlos Garcín`,
      description: post.excerpt,
      path: `/${post.slug}`,
      keywords: post.keywords,
      image: `https://garcin.cl${post.image}`,
    });
    window.scrollTo(0, 0);
  }, [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    const others = BLOG_POSTS.filter((p) => p.slug !== post.slug);
    return [...others].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [post]);

  if (!post) return <Navigate to="/rompiendo-el-techo" replace />;

  const meta = CATEGORY_STYLES[post.category] || DEFAULT_CATEGORY_STYLE;
  const CategoryIcon = meta.icon;

  const articleUrl = `https://garcin.cl/${post.slug}`;
  const imageUrl = `https://garcin.cl${post.image}`;
  const isoDate = toISODate(post.date);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${articleUrl}#article`,
    headline: post.title,
    description: post.excerpt,
    image: imageUrl,
    datePublished: isoDate,
    dateModified: isoDate,
    author: { "@id": "https://garcin.cl/#carlos-garcin", "@type": "Person", name: "Carlos Garcín" },
    publisher: {
      "@type": "Organization",
      name: "Carlos Garcín — Implementador EOS Chile",
      logo: { "@type": "ImageObject", url: "https://garcin.cl/og-image.jpg" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    keywords: post.keywords,
    inLanguage: "es-CL",
  };

  const faqBlock = post.blocks?.find((b) => b.type === "faq");
  const faqSchema = faqBlock && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${articleUrl}#faq`,
    mainEntity: faqBlock.items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
      { "@type": "ListItem", position: 2, name: "Rompiendo el techo", item: "https://garcin.cl/rompiendo-el-techo" },
      { "@type": "ListItem", position: 3, name: post.title, item: articleUrl },
    ],
  };

  return (
    <>
      <Header />
      <main itemScope itemType="https://schema.org/Article">
        <meta itemProp="description" content={post.excerpt} />
        <meta itemProp="datePublished" content={toISODate(post.date)} />
        <section className="bg-[#0A1128] pt-28 pb-16" data-testid="blog-post-hero">
          <div className="container-eos">
            <Link
              to="/rompiendo-el-techo"
              data-testid="blog-post-back-link"
              className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-slate-400 hover:text-[#F26522] transition-colors mb-8"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Volver a todos los artículos
            </Link>

            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-slate-400">
                Rompiendo el techo
              </span>
              <span
                data-testid="blog-post-category-chip"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide"
                style={{ backgroundColor: meta.accent, color: "#fff" }}
              >
                <CategoryIcon className="w-3 h-3" strokeWidth={2.5} />
                {post.category}
              </span>
            </div>

            <h1 itemProp="headline" className="mt-4 font-display font-extrabold text-white leading-[1.1] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              {renderTitle(post.title, post.highlight)}
            </h1>

            <p className="mt-5 text-slate-400 text-sm font-medium">
              {post.date} · {post.readTime}
            </p>
          </div>
        </section>

        {post.image && (
          <div className="lg:hidden bg-white pt-8" data-testid="blog-post-image-mobile-wrapper">
            <div className="container-eos">
              <div className="reveal w-full max-w-[416px] mx-auto rounded-2xl overflow-hidden border border-slate-200 shadow-sm aspect-square">
                <img
                  src={post.image}
                  alt={post.title}
                  data-testid="blog-post-image-mobile"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        )}

        <section className="pt-8 sm:pt-10 lg:pt-12 pb-20 sm:pb-24 lg:pb-28 bg-white" data-testid="blog-post-content">
          <div className="container-eos">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-14">
              <div className="reveal flex-1 max-w-2xl">
                {post.blocks ? (
                  <>
                    <BlogBlocks blocks={post.blocks} />
                    {post.content && (() => {
                      const [signatureLine, ctaLine] = post.content.split("\n");
                      return (
                        <p className="mt-10 pt-6 border-t border-slate-200 text-slate-700 leading-relaxed text-base sm:text-lg">
                          <Link
                            to="/implementador-eos"
                            data-testid="blog-post-author-link"
                            className="hover:opacity-80 transition-opacity"
                          >
                            <strong className="font-semibold text-navy-800">{signatureLine}</strong>
                          </Link>
                          <br />
                          <Link
                            to="/contacto"
                            data-testid="blog-post-contact-link"
                            className="text-[#F26522] font-bold hover:underline"
                          >
                            {ctaLine}
                          </Link>
                        </p>
                      );
                    })()}
                  </>
                ) : (
                  post.content.split("\n\n").map((para, i) => {
                    if (para.includes("Carlos Garcín - Professional EOS Implementer")) {
                      const [signatureLine, ctaLine] = para.split("\n");
                      return (
                        <p key={i} className="mt-8 pt-6 border-t border-slate-200 text-slate-700 leading-relaxed text-base sm:text-lg">
                          <Link
                            to="/implementador-eos"
                            data-testid="blog-post-author-link"
                            className="hover:opacity-80 transition-opacity"
                          >
                            <strong className="font-semibold text-navy-800">{signatureLine}</strong>
                          </Link>
                          <br />
                          <Link
                            to="/contacto"
                            data-testid="blog-post-contact-link"
                            className="text-[#F26522] font-bold hover:underline"
                          >
                            {ctaLine}
                          </Link>
                        </p>
                      );
                    }
                    return (
                      <p key={i} className="mt-5 first:mt-0 text-slate-700 leading-relaxed text-base sm:text-lg">
                        {renderBoldedText(para)}
                      </p>
                    );
                  })
                )}
              </div>

              {post.image && (
                <div className="reveal w-full max-w-[416px] mx-auto lg:w-[364px] lg:shrink-0 lg:sticky lg:top-28">
                  <div className="hidden lg:block rounded-2xl overflow-hidden border border-slate-200 shadow-sm aspect-square">
                    <img
                      src={post.image}
                      alt={post.title}
                      data-testid="blog-post-image"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {relatedPosts.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-slate-200" data-testid="blog-post-related-articles">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-4">
                        También te puede interesar
                      </p>
                      <div className="space-y-4">
                        {relatedPosts.map((rp) => {
                          const rpMeta = CATEGORY_STYLES[rp.category] || DEFAULT_CATEGORY_STYLE;
                          const RpIcon = rpMeta.icon;
                          return (
                            <Link
                              key={rp.slug}
                              to={`/${rp.slug}`}
                              data-testid={`blog-post-related-link-${rp.slug}`}
                              className="group flex items-start gap-2.5"
                            >
                              <RpIcon
                                className="w-4 h-4 mt-0.5 shrink-0"
                                strokeWidth={2.25}
                                style={{ color: rpMeta.accent }}
                              />
                              <span className="text-sm font-semibold text-navy-800 leading-snug group-hover:text-[#F26522] transition-colors">
                                {rp.title}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
    </>
  );
}
